import { useCallback, useState } from 'react';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import { useTranslation } from 'react-i18next';
import './Onboarding.css';

function OnboardingWizard({ onComplete, onCancel, isCreating }) {
    const { t, i18n } = useTranslation();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        // Step 1
        title: '',
        synopsis: '',
        genre: '',
        targetAudience: '',
        experienceLevel: '',
        // Step 2
        setting: '',
        characters: '',
        tone: '',
        // Step 3
        mentor: ''
    });

    const updateFormData = useCallback((data) => {
        setFormData(prev => ({ ...prev, ...data }));
    }, []);

    const handleNext = useCallback(() => {
        setCurrentStep(prev => Math.min(prev + 1, 3));
    }, []);

    const handleBack = useCallback(() => {
        if (currentStep === 1) {
            if (onCancel) {
                onCancel();
            }
            return;
        }
        setCurrentStep(prev => Math.max(prev - 1, 1));
    }, [currentStep, onCancel]);

    const handleComplete = useCallback(() => {
        onComplete(formData);
    }, [formData, onComplete]);

    const isStepValid = () => {
        switch (currentStep) {
            case 1:
                return formData.title.trim() !== '' &&
                    formData.synopsis.trim() !== '' &&
                    formData.genre !== '';
            case 2:
                return true; // Optional step
            case 3:
                return formData.mentor !== '';
            default:
                return false;
        }
    };

    const isRtl = i18n.dir() === 'rtl';
    const backArrow = isRtl ? '→' : '←';
    const nextArrow = isRtl ? '←' : '→';

    return (
        <div className="onboarding">
            <div className="onboarding-background">
                <div className="bg-gradient-1"></div>
                <div className="bg-gradient-2"></div>
                <div className="bg-noise"></div>
            </div>

            <div className="onboarding-container">
                <header className="onboarding-header">
                    <h1 className="onboarding-title">
                        <span className="title-icon">✒️</span>
                        Share-Pen
                    </h1>
                    <p className="onboarding-subtitle">{t("onboarding.subtitle")}</p>
                </header>

                <div className="onboarding-card">
                    {/* Progress bar */}
                    <div className="progress-header">
                        <div className="progress-bar">
                            <div className={`progress-segment ${currentStep >= 1 ? 'completed' : ''}`}></div>
                            <div className={`progress-segment ${currentStep >= 2 ? 'completed' : ''}`}></div>
                            <div className={`progress-segment ${currentStep >= 3 ? 'completed' : ''}`}></div>
                        </div>
                        <span className="progress-text">{t("onboarding.stepOf", { current: currentStep, total: 3 })}</span>
                    </div>

                    {/* Step content */}
                    <div className="step-content">
                        {currentStep === 1 && (
                            <StepOne formData={formData} updateFormData={updateFormData} />
                        )}
                        {currentStep === 2 && (
                            <StepTwo formData={formData} updateFormData={updateFormData} />
                        )}
                        {currentStep === 3 && (
                            <StepThree formData={formData} updateFormData={updateFormData} />
                        )}
                    </div>

                    {/* Navigation */}
                    <div className="step-navigation">
                        <button
                            type="button"
                            className="btn btn-ghost step-nav-btn"
                            onClick={handleBack}
                            disabled={isCreating}
                        >
                            <span className="step-nav-btn__arrow" aria-hidden="true">{backArrow}</span>
                            <span className="step-nav-btn__label">{t('onboarding.back')}</span>
                        </button>

                        {currentStep < 3 ? (
                            <button
                                type="button"
                                className="btn btn-primary step-nav-btn"
                                onClick={handleNext}
                                disabled={!isStepValid()}
                            >
                                <span className="step-nav-btn__label">{t('onboarding.next')}</span>
                                <span className="step-nav-btn__arrow" aria-hidden="true">{nextArrow}</span>
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-primary step-nav-btn"
                                onClick={handleComplete}
                                disabled={!isStepValid() || isCreating}
                            >
                                {isCreating ? (
                                    <>
                                        <span className="spinner"></span>{t('onboarding.creatingWorkspace')}</>
                                ) : (
                                    <>
                                        <span className="step-nav-btn__label">{t('onboarding.createWorkspace')}</span>
                                        <span className="step-nav-btn__arrow" aria-hidden="true">{nextArrow}</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OnboardingWizard;
